/**
 * Minimal, dependency-free XML scanning for OOXML (.xlsx) parts.
 *
 * The browser's `DOMParser` is unavailable in bun's test runtime and in
 * Cloudflare Workers, and pulling in a DOM shim just to read a handful of
 * flat, well-known element shapes (`<si>`, `<row>`, `<c>`, `<xf>`) would make
 * this package non-portable. OOXML worksheet XML is shallow and regular, so a
 * targeted scanner is both sufficient and faster than a full DOM build.
 *
 * These helpers decode the five predefined XML entities plus numeric character
 * references, matching what a real DOM parser returns for text content.
 */

/** Decode XML text: the five predefined entities + decimal/hex numeric refs. */
export function decodeXmlEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(
    /&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g,
    (whole, body: string) => {
      if (body[0] === '#') {
        const codePoint =
          body[1] === 'x' || body[1] === 'X'
            ? parseInt(body.slice(2), 16)
            : parseInt(body.slice(1), 10);
        if (
          !Number.isFinite(codePoint) ||
          codePoint < 0 ||
          codePoint > 0x10ffff
        ) {
          return whole;
        }
        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return whole;
        }
      }
      switch (body) {
        case 'amp':
          return '&';
        case 'lt':
          return '<';
        case 'gt':
          return '>';
        case 'quot':
          return '"';
        case 'apos':
          return "'";
        default:
          return whole;
      }
    },
  );
}

/** Read a single attribute's value (decoded) from an element's attribute text. */
export function getAttr(attrs: string, name: string): string | null {
  // Match name="..." or name='...'; `name` is a fixed literal (no regex meta).
  const re = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`);
  const m = re.exec(attrs);
  if (!m) return null;
  // `.at()` is typed `string | undefined` (unlike `m[2]`, typed `string`),
  // which is the runtime truth: an alternation branch that did not match is
  // undefined. Exactly one of the two quote groups is ever populated.
  return decodeXmlEntities(m.at(2) ?? m.at(3) ?? '');
}

export interface XmlElement {
  /** Raw attribute text (everything between the tag name and `>`). */
  attrs: string;
  /** Inner XML (empty string for a self-closing element). */
  inner: string;
}

/**
 * Iterate every `<tag ...>...</tag>` (and self-closing `<tag .../>`) at any
 * depth within `xml`, in document order. Does not recurse into nested same-name
 * tags beyond matching the first closing tag — OOXML parts never nest `<si>`,
 * `<row>`, `<c>`, `<xf>` or `<numFmt>` inside themselves, so a non-greedy first
 * match is correct for every element this package reads.
 */
export function* scanElements(xml: string, tag: string): Generator<XmlElement> {
  // Open tag: `<tag` whose name is terminated by a space, `/`, or `>` (the
  // lookahead stops `<c>` from matching `<cellXfs>`); lazy attrs so the trailing
  // `/>` of a self-closing element is captured by the slash group, not attrs.
  const openRe = new RegExp(`<${tag}(?=[\\s/>])([^>]*?)(/?)>`, 'g');
  let m: RegExpExecArray | null;
  while ((m = openRe.exec(xml)) !== null) {
    const attrs = m[1];
    const selfClosing = m[2] === '/';
    if (selfClosing) {
      yield { attrs, inner: '' };
      continue;
    }
    const closeTag = `</${tag}>`;
    const closeIdx = xml.indexOf(closeTag, openRe.lastIndex);
    if (closeIdx === -1) {
      // Unterminated element — treat the rest of the document as its content.
      yield { attrs, inner: xml.slice(openRe.lastIndex) };
      return;
    }
    yield { attrs, inner: xml.slice(openRe.lastIndex, closeIdx) };
    openRe.lastIndex = closeIdx + closeTag.length;
  }
}

/**
 * The decoded text content of the FIRST `<tag>...</tag>` inside `xml`, or
 * `null` when no such element exists. A self-closing `<tag/>` yields `''`.
 */
export function firstTagText(xml: string, tag: string): string | null {
  for (const el of scanElements(xml, tag)) {
    return decodeXmlEntities(el.inner);
  }
  return null;
}

/** Concatenate the decoded text of every `<tag>...</tag>` inside `xml`. */
export function concatTagText(xml: string, tag: string): string {
  let out = '';
  for (const el of scanElements(xml, tag)) {
    out += decodeXmlEntities(el.inner);
  }
  return out;
}
